import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../../supabase';
import CountUp from 'react-countup';
import FlipMove from 'react-flip-move';
import styles from './ProductRanking.module.css';
import TabMenu from '../components/TabMenu';

// 애니메이션을 위한 랭킹 아이템 컴포넌트
const RankingItem = React.forwardRef(({ idea, index, prevValues }, ref) => (
  <Link 
    ref={ref}
    to={`/project/plabthon/${idea.id}`}
    className={styles.rankingItem}
  >
    <div className={styles.rankInfo}>
      <span className={`${styles.rank} ${index < 3 ? styles.medalRank : ''}`}>
        {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
      </span>
      <div className={styles.productInfo}>
        <span className={styles.productTitle}>{idea.short_name}</span>
        <span className={styles.teamInfo}>
          {idea.owner_name}
          {idea.team_members_names && (
            <span className={styles.teamMembers}>
              with {idea.team_members_names}
            </span>
          )}
        </span>
      </div>
    </div>
    <p className={styles.totalValue}>
      <CountUp
        start={prevValues[idea.id] || 0}
        end={idea.total_investment || 0}
        duration={2.5}
        separator=","
        suffix="원"
        useEasing={true}
      />
    </p>
  </Link>
));

function ProductRanking() {
  const [productRankings, setProductRankings] = useState([]);
  const [prevValues, setPrevValues] = useState({});

  useEffect(() => {
    fetchProductRankings();

    // Realtime subscription 설정
    const channel = supabase.channel('public:ideas')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'ideas',
        },
        (payload) => {
          // 이전 값 저장
          setProductRankings(prevRankings => {
            const oldIdea = prevRankings.find(idea => idea.id === payload.new.id);
            if (oldIdea) {
              setPrevValues(prev => ({
                ...prev,
                [payload.new.id]: oldIdea.total_investment || 0
              }));
            }

            const updatedRankings = prevRankings.map(idea => 
              idea.id === payload.new.id 
                ? { ...idea, total_investment: payload.new.total_investment }
                : idea
            );
            return updatedRankings.sort((a, b) => 
              (b.total_investment || 0) - (a.total_investment || 0)
            );
          });
        }
      )
      .subscribe();

    // 컴포넌트 언마운트 시 구독 해제
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchProductRankings = async () => {
    try {
      const { data: ideas, error } = await supabase
        .from('ideas')
        .select(`
          id,
          title,
          total_investment,
          user_id,
          status,
          short_name
        `)
        .eq('status', 'in_progress')
        .order('total_investment', { ascending: false });

      if (error) throw error;

      const ideasWithOwners = await Promise.all(
        ideas.map(async (idea) => {
          const { data: profile } = await supabase
            .from('user_profiles')
            .select('name')
            .eq('user_id', idea.user_id)
            .single();

          return {
            ...idea,
            owner_name: profile?.name || '익명'
          };
        })
      );

      setProductRankings(ideasWithOwners);
    } catch (error) {
      console.error('Error fetching product rankings:', error);
    }
  };

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1>프로덕트 순위</h1>
      </header>
      
      <main className={styles.main}>
        <FlipMove
          className={styles.rankingList}
          duration={750}
          easing="ease-out"
          enterAnimation="fade"
          leaveAnimation="fade"
        >
          {productRankings.length > 0 ? (
            productRankings.map((idea, index) => (
              <RankingItem
                key={idea.id}
                idea={idea}
                index={index}
                prevValues={prevValues}
              />
            ))
          ) : (
            <div className={styles.noData}>
              진행중인 프로덕트가 없습니다.
            </div>
          )}
        </FlipMove>
      </main>

      <TabMenu />
    </div>
  );
}

export default ProductRanking; 